import {
  DestroyRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive({
  selector: '[scrollbarOverContent]',
  standalone: true,
})
export class ScrollbarOverContentDirective implements OnInit, OnDestroy {
  private static readonly MIN_THUMB_HEIGHT = 56;
  public readonly elementRef = inject(ElementRef);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private destroyRef = inject(DestroyRef);
  /**
   * Camada `position: sticky` inserida como PRIMEIRO filho do contêiner com
   * scroll. O compositor do navegador a mantém fixada no topo da viewport sem
   * JS, então o thumb (filho absoluto desta camada) NÃO rola junto com o
   * conteúdo. É isto que elimina o atraso: o scroll nativo é composto numa
   * thread separada e o evento `scroll` chega ao JS tarde demais para
   * reposicionar um thumb que está dentro do fluxo rolável — com a camada
   * sticky, o thumb já fica no lugar certo de graça. Mesma estratégia do
   * simple-grid-list.
   */
  private stickyLayer: HTMLDivElement | null = null;
  /** Thumb visual (a barrinha em si). Filho absoluto de {@link stickyLayer}. */
  private scrollbar: HTMLDivElement | null = null;
  private isDragging = false;
  private isMouseOut = false;
  private lastScrollHeight = 0;
  private lastClientHeight = 0;
  private thumbHeight = 0;

  public ngOnInit(): void {
    if (typeof document === 'undefined') {
      return;
    }
    this.stickyLayer = document.createElement('div');
    this.scrollbar = document.createElement('div');
    this.setStyles();
    this.scrollbar.addEventListener('mousedown', this.mouseDown);
    this.stickyLayer.appendChild(this.scrollbar);
    // Primeiro filho: necessário para a camada sticky fixar no topo desde
    // scrollTop = 0. Como tem height 0, não ocupa espaço no fluxo.
    const host = this.elementRef.nativeElement;
    host.insertBefore(this.stickyLayer, host.firstChild);
    requestAnimationFrame(() => this.updateScrollbarSize());
  }

  public ngOnDestroy(): void {
    this.scrollbar?.removeEventListener('mousedown', this.mouseDown);
    this.stickyLayer?.remove();
    this.stickyLayer = null;
    this.scrollbar = null;
  }

  public mouseDown = (event: MouseEvent): void => {
    event.preventDefault();
    this.isDragging = true;

    const scrollTop = this.elementRef.nativeElement.scrollTop;
    const height = this.elementRef.nativeElement.scrollHeight;
    const clientHeight = this.elementRef.nativeElement.clientHeight;
    const scrollHeight = height - clientHeight;
    const trackSpace = clientHeight - this.thumbHeight;

    const start = event.clientY;

    const mouseMove = fromEvent(document, 'mousemove').pipe(takeUntilDestroyed(this.destroyRef));
    const mouseUp = fromEvent(document, 'mouseup').pipe(takeUntilDestroyed(this.destroyRef));

    const subMouseMove = mouseMove.subscribe((e: any) => {
      const y = e.clientY - start;
      // Mover o thumb por todo o trackSpace equivale a rolar todo o scrollHeight.
      const scrollPx = trackSpace > 0 ? (y / trackSpace) * scrollHeight : 0;
      this.elementRef.nativeElement.scrollTop = scrollTop + scrollPx;
      // Atualiza de forma síncrona (sem rAF) para o thumb não atrasar um frame.
      this.updateScrollbarTop();
    });

    const subMouseUp = mouseUp.subscribe(() => {
      subMouseMove.unsubscribe();
      subMouseUp.unsubscribe();
      this.isDragging = false;
      if (this.isMouseOut) this.mouseLeave();
    });
  }

  @HostListener('mouseenter')
  public mouseEnter(): void {
    this.isMouseOut = false;
    this.renderer.setStyle(this.scrollbar, 'opacity', '1');
  }

  @HostListener('mouseleave')
  public mouseLeave(): void {
    this.isMouseOut = true;
    if (!this.isDragging) this.renderer.setStyle(this.scrollbar, 'opacity', '0');
  }

  @HostListener('scroll')
  public scroll(): void {
    if (this.isDragging || !this.scrollbar) return;
    // Atualização síncrona: a camada sticky já fixa o thumb na viewport via
    // compositor, então aqui só atualizamos a posição DENTRO da pista. Sem rAF
    // para não introduzir atraso. Só recalcula o tamanho quando as dimensões
    // realmente mudam, para o scroll permanecer barato.
    const el = this.elementRef.nativeElement;
    if (el.scrollHeight !== this.lastScrollHeight || el.clientHeight !== this.lastClientHeight) {
      this.updateScrollbarSize();
    }
    this.updateScrollbarTop();
  }

  private updateScrollbarTop(): void {
    if (!this.scrollbar) return;
    const el = this.elementRef.nativeElement;
    const scrollHeight = el.scrollHeight - el.clientHeight;
    if (scrollHeight <= 0) {
      this.renderer.setStyle(this.scrollbar, 'transform', 'translateY(0px)');
      return;
    }
    const trackSpace = el.clientHeight - this.thumbHeight;
    const scrollPx = trackSpace > 0 ? (el.scrollTop / scrollHeight) * trackSpace : 0;
    // Como o thumb está pinado pela camada sticky (não rola com o conteúdo),
    // o translateY é só a posição na pista — sem somar scrollTop. Usamos
    // `transform` (camada de composição) para evitar reflow a cada scroll.
    this.renderer.setStyle(this.scrollbar, 'transform', `translateY(${scrollPx}px)`);
  }

  private updateScrollbarSize(): void {
    if (!this.scrollbar) return;
    const el = this.elementRef.nativeElement;
    const clientHeight = el.clientHeight;
    const scrollHeight = el.scrollHeight;
    this.lastScrollHeight = scrollHeight;
    this.lastClientHeight = clientHeight;

    const hasOverflow = scrollHeight > clientHeight;
    if (!hasOverflow) {
      this.thumbHeight = 0;
      this.renderer.setStyle(this.scrollbar, 'height', '0');
      this.renderer.setStyle(this.scrollbar, 'pointer-events', 'none');
      return;
    }
    this.renderer.setStyle(this.scrollbar, 'pointer-events', 'auto');
    const proportion = clientHeight / scrollHeight;
    const thumbHeight = Math.max(
      ScrollbarOverContentDirective.MIN_THUMB_HEIGHT,
      Math.floor(proportion * clientHeight)
    );
    // Cacheia a altura do thumb para o `updateScrollbarTop` não precisar ler
    // `clientHeight` (que forçaria reflow) a cada evento de scroll.
    this.thumbHeight = thumbHeight;
    this.renderer.setStyle(this.scrollbar, 'height', `${thumbHeight}px`);
    // Reposiciona já com o novo tamanho para a pista não “saltar”.
    this.updateScrollbarTop();
  }

  private setStyles(): void {
    this.renderer.addClass(this.elementRef.nativeElement, 'scrollbar-none');
    this.renderer.addClass(this.elementRef.nativeElement, 'relative');
    this.renderer.addClass(this.elementRef.nativeElement, 'overflow-auto');

    // Camada sticky: ocupa 0px de altura no fluxo, fixa no topo da viewport do
    // contêiner e deixa cliques passarem (só o thumb captura ponteiro).
    this.renderer.setStyle(this.stickyLayer, 'position', 'sticky');
    this.renderer.setStyle(this.stickyLayer, 'top', '0');
    this.renderer.setStyle(this.stickyLayer, 'height', '0');
    this.renderer.setStyle(this.stickyLayer, 'zIndex', '100');
    this.renderer.setStyle(this.stickyLayer, 'pointer-events', 'none');

    this.renderer.setStyle(this.scrollbar, 'padding', '3px');
    this.renderer.setStyle(this.scrollbar, 'width', '15px');
    this.renderer.setStyle(this.scrollbar, 'height', '0');
    this.renderer.setStyle(this.scrollbar, 'position', 'absolute');
    // A camada sticky é um elemento em fluxo, então o bloco de contenção do
    // thumb é a CAIXA DE CONTEÚDO do host (dentro do padding). Com `right/top: 0`
    // o thumb ficaria recuado pelo padding do host (e a pista, calculada a
    // partir do `clientHeight` que INCLUI o padding, ficaria desalinhada).
    // Compensamos com offsets negativos iguais ao padding para o thumb pinar
    // sempre na borda do host, independente do padding (host sem padding → 0).
    const hostStyle = getComputedStyle(this.elementRef.nativeElement);
    const paddingRight = parseFloat(hostStyle.paddingRight) || 0;
    const paddingTop = parseFloat(hostStyle.paddingTop) || 0;
    this.renderer.setStyle(this.scrollbar, 'right', `${-paddingRight}px`);
    this.renderer.setStyle(this.scrollbar, 'top', `${-paddingTop}px`);
    this.renderer.setStyle(this.scrollbar, 'opacity', '0');
    this.renderer.setStyle(this.scrollbar, 'cursor', 'pointer');
    this.renderer.setStyle(this.scrollbar, 'pointer-events', 'none');
    this.renderer.setStyle(this.scrollbar, 'willChange', 'transform');

    const scrollbarThumb = document.createElement('div');
    this.renderer.addClass(scrollbarThumb, 'bg-scroll-opacity');
    this.renderer.addClass(scrollbarThumb, 'hover:bg-scroll');
    this.renderer.addClass(scrollbarThumb, 'w-full');
    this.renderer.addClass(scrollbarThumb, 'h-full');
    this.renderer.addClass(scrollbarThumb, 'border-radius-8');
    this.renderer.addClass(scrollbarThumb, 'cursor-pointer');

    this.scrollbar!.appendChild(scrollbarThumb);
  }
}
